import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Edit,
  X,
  Upload,
  FileText,
  Image,
  Calendar,
  Link as LinkIcon,
  Settings
} from "lucide-react";
import DragDropUpload from "../components/DragDropUpload";

const committeeInfo = {
  "mosque-dawah": { name: "Mosque and Da'wah", icon: "🕌", color: "from-emerald-500 to-teal-600", bgColor: "bg-emerald-50", hasMedia: true },
  "project-maintenance": { name: "Project and Maintenance", icon: "🔧", color: "from-blue-500 to-indigo-600", bgColor: "bg-blue-50", hasMedia: false },
  "welfare-publicity": { name: "Welfare and Publicity", icon: "💚", color: "from-pink-500 to-rose-600", bgColor: "bg-pink-50", hasMedia: false },
  "finance-investment": { name: "Finance and Investment", icon: "💰", color: "from-amber-500 to-orange-600", bgColor: "bg-amber-50", hasMedia: false },
  "security-ethic": { name: "Security and Ethics", icon: "🛡️", color: "from-purple-500 to-violet-600", bgColor: "bg-purple-50", hasMedia: false }
};

const AdminCommitteeActivities = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const committee = committeeInfo[id];

  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("posts");
  
  // Post form state
  const [showPostForm, setShowPostForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postFormData, setPostFormData] = useState({
    title: "",
    content: "",
    category: "activity",
    image_url: ""
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageUploadMode, setImageUploadMode] = useState("file");

  // Event form state
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventFormData, setEventFormData] = useState({
    title: "",
    description: "",
    date: "",
    location: ""
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/login");
      return;
    }
    fetchData();
  }, [id, user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [postsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/posts?committee=${id}`),
        axios.get(`${API}/events?committee=${id}`)
      ]);
      setPosts(postsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Post handlers
  const handlePostSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      let imageUrl = postFormData.image_url;
      
      if (imageFile) {
        const formData = new FormData();
        formData.append("title", `Post Image - ${postFormData.title}`);
        formData.append("description", "Post image");
        formData.append("media_type", "image");
        formData.append("committee", id);
        formData.append("file", imageFile);
        
        const mediaRes = await axios.post(`${API}/media`, formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        });
        imageUrl = mediaRes.data.file_url;
      }

      const data = {
        ...postFormData,
        committee: id,
        image_url: imageUrl
      };

      if (editingPost) {
        await axios.put(`${API}/posts/${editingPost.post_id}`, data, { withCredentials: true });
        toast.success("Post updated!");
      } else {
        await axios.post(`${API}/posts`, data, { withCredentials: true });
        toast.success("Post created!");
      }
      
      fetchData();
      resetPostForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save post");
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await axios.delete(`${API}/posts/${postId}`, { withCredentials: true });
      toast.success("Post deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setPostFormData({
      title: post.title,
      content: post.content,
      category: post.category,
      image_url: post.image_url || ""
    });
    setImageUploadMode(post.image_url ? "url" : "file");
    setShowPostForm(true);
  };

  const resetPostForm = () => {
    setShowPostForm(false);
    setEditingPost(null);
    setPostFormData({ title: "", content: "", category: "activity", image_url: "" });
    setImageFile(null);
    setImageUploadMode("file");
  };

  // Event handlers
  const handleEventSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...eventFormData,
        committee: id,
        date: new Date(eventFormData.date).toISOString()
      };

      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.event_id}`, data, { withCredentials: true });
        toast.success("Event updated!");
      } else {
        await axios.post(`${API}/events`, data, { withCredentials: true });
        toast.success("Event created!");
      }
      
      fetchData();
      resetEventForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save event");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      toast.success("Event deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventFormData({
      title: event.title,
      description: event.description,
      date: new Date(event.date).toISOString().slice(0, 16),
      location: event.location
    });
    setShowEventForm(true);
  };

  const resetEventForm = () => {
    setShowEventForm(false);
    setEditingEvent(null);
    setEventFormData({ title: "", description: "", date: "", location: "" });
  };

  if (!committee) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Card className="p-12 text-center rounded-3xl max-w-md">
          <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Committee Not Found</h2>
          <p className="text-gray-600 mb-6">The committee you're looking for doesn't exist.</p>
          <Button asChild className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl">
            <Link to="/admin">Back to Admin</Link>
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className={`bg-gradient-to-br ${committee.color} text-white`}>
        <div className="container-custom py-8">
          <Link to="/admin" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors group">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Admin Dashboard
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-3xl">
              {committee.icon}
            </div>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-xs font-medium mb-2">
                <Settings className="w-3 h-3" />
                Committee Management
              </div>
              <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="committee-title">
                {committee.name}
              </h1>
              <p className="text-white/80">Manage activities, posts, and events</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Section Tabs */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Button
            onClick={() => setActiveSection("posts")}
            className={`rounded-xl px-6 py-2.5 ${
              activeSection === "posts" 
                ? "bg-primary-900 hover:bg-primary-800 text-white" 
                : "bg-white hover:bg-gray-50 text-gray-700 border"
            }`}
            data-testid="section-posts-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Posts ({posts.length})
          </Button>
          <Button
            onClick={() => setActiveSection("events")}
            className={`rounded-xl px-6 py-2.5 ${
              activeSection === "events" 
                ? "bg-primary-900 hover:bg-primary-800 text-white" 
                : "bg-white hover:bg-gray-50 text-gray-700 border"
            }`}
            data-testid="section-events-btn"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Events ({events.length})
          </Button>
          {committee.hasMedia && (
            <Button
              asChild
              className="rounded-xl px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border"
              data-testid="manage-media-btn"
            >
              <Link to="/admin/media-gallery">
                <Image className="w-4 h-4 mr-2" />
                Manage Media Gallery
              </Link>
            </Button>
          )}
        </div>

        {/* Posts Section */}
        {activeSection === "posts" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">Committee Posts</h2>
                <p className="text-gray-600">Create and manage posts for this committee.</p>
              </div>
              <Button onClick={() => setShowPostForm(true)} className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl" data-testid="create-committee-post-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Post
              </Button>
            </div>

            {showPostForm && (
              <Card className="p-6 rounded-3xl shadow-lg border-0">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-heading text-xl font-semibold text-gray-900">
                    {editingPost ? "Edit Post" : "Create New Post"}
                  </h3>
                  <Button variant="ghost" size="icon" onClick={resetPostForm} className="rounded-full">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <form onSubmit={handlePostSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700 font-medium">Title</Label>
                      <Input
                        value={postFormData.title}
                        onChange={(e) => setPostFormData({ ...postFormData, title: e.target.value })}
                        placeholder="Post title"
                        required
                        className="h-12 rounded-xl"
                        data-testid="committee-post-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 font-medium">Category</Label>
                      <Select value={postFormData.category} onValueChange={(v) => setPostFormData({ ...postFormData, category: v })}>
                        <SelectTrigger className="h-12 rounded-xl" data-testid="committee-post-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="activity">Activity</SelectItem>
                          <SelectItem value="news">News</SelectItem>
                          <SelectItem value="announcement">Announcement</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">Content</Label>
                    <Textarea
                      value={postFormData.content}
                      onChange={(e) => setPostFormData({ ...postFormData, content: e.target.value })}
                      placeholder="Write your post content..."
                      rows={6}
                      required
                      className="rounded-xl resize-none"
                      data-testid="committee-post-content-input"
                    />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-700 font-medium">Post Image (Optional)</Label>
                      <div className="flex gap-1 p-1 rounded-xl bg-gray-100">
                        <button
                          type="button"
                          onClick={() => { setImageUploadMode("file"); setPostFormData({ ...postFormData, image_url: "" }); }}
                          className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors ${imageUploadMode === "file" ? "bg-white shadow-sm text-gray-900" : "text-gray-600 hover:text-gray-900"}`}
                          data-testid="image-mode-file"
                        >
                          <Upload className="w-3 h-3 inline mr-1" />
                          Upload
                        </button>
                        <button
                          type="button"
                          onClick={() => { setImageUploadMode("url"); setImageFile(null); }}
                          className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors ${imageUploadMode === "url" ? "bg-white shadow-sm text-gray-900" : "text-gray-600 hover:text-gray-900"}`}
                          data-testid="image-mode-url"
                        >
                          <LinkIcon className="w-3 h-3 inline mr-1" />
                          URL
                        </button>
                      </div>
                    </div>
                    
                    {imageUploadMode === "file" ? (
                      <DragDropUpload
                        onFileSelect={setImageFile}
                        currentFile={imageFile}
                        mediaType="image"
                        maxSize={50 * 1024 * 1024}
                      />
                    ) : (
                      <Input
                        value={postFormData.image_url}
                        onChange={(e) => setPostFormData({ ...postFormData, image_url: e.target.value })}
                        placeholder="https://example.com/image.jpg"
                        className="h-12 rounded-xl"
                        data-testid="committee-post-image-url-input"
                      />
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl px-6" disabled={uploading} data-testid="committee-post-submit-btn">
                      {uploading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : editingPost ? "Update Post" : "Create Post"}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetPostForm} className="rounded-xl px-6">
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            )}

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="p-6 rounded-2xl animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                  </Card>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <Card className="p-16 text-center rounded-3xl border-dashed">
                <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
                  <FileText className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">No Posts Yet</h3>
                <p className="text-gray-500">Create your first post for this committee!</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <Card key={post.post_id} className="p-5 rounded-2xl hover:shadow-lg transition-shadow" data-testid={`committee-admin-post-${post.post_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-grow">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            post.category === 'news' ? 'bg-blue-100 text-blue-700' :
                            post.category === 'announcement' ? 'bg-amber-100 text-amber-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {post.category}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1">{post.title}</h3>
                        <p className="text-sm text-gray-600 line-clamp-2">{post.content}</p>
                        <p className="text-xs text-gray-400 mt-3">
                          {format(new Date(post.created_at), "MMM d, yyyy")} by {post.author_name}
                        </p>
                      </div>
                      {post.image_url && (
                        <img src={post.image_url} alt="" className="w-24 h-24 rounded-xl object-cover ml-4 shadow-sm" />
                      )}
                      <div className="flex gap-2 ml-4">
                        <Button variant="ghost" size="icon" onClick={() => handleEditPost(post)} className="rounded-xl hover:bg-primary-50">
                          <Edit className="w-4 h-4 text-primary-900" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeletePost(post.post_id)} className="rounded-xl hover:bg-red-50">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Events Section */}
        {activeSection === "events" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">Committee Events</h2>
                <p className="text-gray-600">Create and manage events for this committee.</p>
              </div>
              <Button onClick={() => setShowEventForm(true)} className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl" data-testid="create-committee-event-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Event
              </Button>
            </div>

            {showEventForm && (
              <Card className="p-6 rounded-3xl shadow-lg border-0">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-heading text-xl font-semibold text-gray-900">
                    {editingEvent ? "Edit Event" : "Create New Event"}
                  </h3>
                  <Button variant="ghost" size="icon" onClick={resetEventForm} className="rounded-full">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <form onSubmit={handleEventSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700 font-medium">Title</Label>
                      <Input
                        value={eventFormData.title}
                        onChange={(e) => setEventFormData({ ...eventFormData, title: e.target.value })}
                        placeholder="Event title"
                        required
                        className="h-12 rounded-xl"
                        data-testid="committee-event-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 font-medium">Date & Time</Label>
                      <Input
                        type="datetime-local"
                        value={eventFormData.date}
                        onChange={(e) => setEventFormData({ ...eventFormData, date: e.target.value })}
                        required
                        className="h-12 rounded-xl"
                        data-testid="committee-event-date-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">Location</Label>
                    <Input
                      value={eventFormData.location}
                      onChange={(e) => setEventFormData({ ...eventFormData, location: e.target.value })}
                      placeholder="Event location"
                      required
                      className="h-12 rounded-xl"
                      data-testid="committee-event-location-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">Description</Label>
                    <Textarea
                      value={eventFormData.description}
                      onChange={(e) => setEventFormData({ ...eventFormData, description: e.target.value })}
                      placeholder="Event description..."
                      rows={4}
                      required
                      className="rounded-xl resize-none"
                      data-testid="committee-event-description-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl px-6" data-testid="committee-event-submit-btn">
                      {editingEvent ? "Update Event" : "Create Event"}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetEventForm} className="rounded-xl px-6">
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            )}

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="p-6 rounded-2xl animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                  </Card>
                ))}
              </div>
            ) : events.length === 0 ? (
              <Card className="p-16 text-center rounded-3xl border-dashed">
                <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
                  <Calendar className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">No Events Yet</h3>
                <p className="text-gray-500">Create your first event for this committee!</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {events.map((event) => (
                  <Card key={event.event_id} className="p-5 rounded-2xl hover:shadow-lg transition-shadow" data-testid={`committee-admin-event-${event.event_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex gap-4">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex flex-col items-center justify-center flex-shrink-0 shadow-lg">
                          <span className="text-xl font-bold text-white leading-none">{format(new Date(event.date), "d")}</span>
                          <span className="text-xs text-primary-100 uppercase">{format(new Date(event.date), "MMM")}</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">{event.title}</h3>
                          <p className="text-sm text-gray-600 line-clamp-2">{event.description}</p>
                          <p className="text-xs text-gray-400 mt-2">{event.location}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEditEvent(event)} className="rounded-xl hover:bg-primary-50">
                          <Edit className="w-4 h-4 text-primary-900" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteEvent(event.event_id)} className="rounded-xl hover:bg-red-50">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCommitteeActivities;
